import { useEffect, useState, type ReactNode } from "react";

export interface WriteConfirmProps {
  /** Title at the top of the dialog. */
  title: string;
  /** Free-form body explaining the change in plain English. */
  body: ReactNode;
  /** Field-change summary lines, rendered as "Label: old → new". */
  changes?: Array<{ label: string; from: string; to: string }>;
  /** Confirm button label. Defaults to "CONFIRM". */
  confirmLabel?: string;
  /** Called when CONFIRM is clicked. May throw — error shown inline. */
  onConfirm: () => Promise<void> | void;
  /** Called when CANCEL is clicked or Escape pressed. */
  onCancel: () => void;
}

/**
 * Centered modal overlay shown before writing to external systems (Zoho etc).
 * Boundary rule: Escape or CANCEL is the only way out — clicking the backdrop
 * does NOT dismiss, since accidental dismissal could obscure that a write
 * was abandoned. CONFIRM is disabled while the action runs.
 */
export function WriteConfirm({
  title,
  body,
  changes,
  confirmLabel = "CONFIRM",
  onConfirm,
  onCancel,
}: WriteConfirmProps) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const handleConfirm = async () => {
    setError(null);
    setRunning(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "write_failed");
      setRunning(false);
    }
    // On success, parent should unmount this component.
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card">
        <div className="write-confirm-title">{title}</div>
        <div className="write-confirm-body">{body}</div>
        {changes && changes.length > 0 && (
          <div className="write-confirm-changes">
            {changes.map((ch, i) => (
              <div className="write-confirm-change" key={i}>
                <span className="write-confirm-change-label">{ch.label}</span>
                <span className="write-confirm-change-from">{ch.from}</span>
                <span className="write-confirm-change-arrow">→</span>
                <span className="write-confirm-change-to">{ch.to}</span>
              </div>
            ))}
          </div>
        )}
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleConfirm}
            disabled={running}
            autoFocus
          >
            {running ? "WRITING…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
